import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, HeartPulse, Megaphone, Rocket } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n/LangContext";

const PRACTICE_ICONS = { health: HeartPulse, institutions: GraduationCap, startups: Rocket, campaigns: Megaphone };
const PRACTICE_IMAGES = { health: IMAGES.researcher, institutions: IMAGES.studentsLab, startups: IMAGES.founder, campaigns: IMAGES.civic };
const PRACTICE_ALTS = {
    health: "Medical researcher working with diagnostic equipment",
    institutions: "Students working in a modern university learning lab",
    startups: "Founder working on a tech product in a sunlit office",
    campaigns: "Civic leader addressing a community audience",
};

const Services = () => {
    const { t, lang } = useLang();
    const s = t.services;

    useEffect(() => {
        document.title = lang === "sw" ? "Huduma — Hallomedics" : "Services — Hallomedics";
    }, [lang]);

    return (
        <div className="pt-28 lg:pt-36" data-testid="services-page">
            <header className="mx-auto max-w-7xl px-5 sm:px-8">
                <p className="eyebrow flex items-center gap-2.5" data-testid="services-eyebrow">
                    <span className="inline-block h-2 w-2 rounded-full bg-teal animate-pulse-dot" />
                    {s.eyebrow}
                </p>
                <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="services-heading" key={lang}>
                    <MaskedLine delay={0.1}>{s.h1a}</MaskedLine>
                    <MaskedLine delay={0.22}>
                        <span className="text-primary dark:text-teal">{s.h1b}</span>
                    </MaskedLine>
                </h1>
                <Reveal delay={0.35}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">{s.intro}</p>
                </Reveal>
            </header>

            <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24" data-testid="practices-list">
                {s.practices.map((p, i) => {
                    const Icon = PRACTICE_ICONS[p.id];
                    return (
                        <section
                            key={p.id}
                            id={p.id}
                            className="scroll-mt-24 border-t border-border py-16 first:border-t-0 first:pt-0 lg:py-20"
                            data-testid={`service-section-${p.id}`}
                        >
                            <div className="grid items-center gap-10 lg:grid-cols-12">
                                <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                                    <Reveal>
                                        <p className="font-mono text-xs uppercase tracking-[0.22em] text-terracotta">{p.num} — {p.flag}</p>
                                        <h2 className="mt-4 flex items-center gap-3 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                                            <Icon className="h-7 w-7 shrink-0 text-primary dark:text-teal" />
                                            {p.title}
                                        </h2>
                                        <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">{p.lead}</p>
                                        <ul className="mt-7 flex max-w-xl flex-wrap gap-2.5" data-testid={`deliverables-${p.id}`}>
                                            {p.deliverables.map((d) => (
                                                <li key={d} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground">
                                                    {d}
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            to={`/contact?service=${p.serviceKey}`}
                                            data-testid={`service-cta-${p.id}`}
                                            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-terracotta dark:text-teal"
                                        >
                                            {s.enquirePrefix} {p.title.toLowerCase()}
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </Reveal>
                                </div>
                                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                                    <Reveal delay={0.12}>
                                        <div className="group overflow-hidden rounded-md border border-border">
                                            <img src={PRACTICE_IMAGES[p.id]} alt={PRACTICE_ALTS[p.id]} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                                        </div>
                                    </Reveal>
                                </div>
                            </div>
                        </section>
                    );
                })}
            </div>

            <section className="border-t border-border bg-secondary/40" data-testid="process-section">
                <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
                    <Reveal>
                        <p className="eyebrow">{s.processEyebrow}</p>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-5">
                        {s.process.map((step, i) => (
                            <Reveal key={step} delay={i * 0.07}>
                                <div className="h-full bg-card px-6 py-8" data-testid={`process-step-${i}`}>
                                    <p className="font-mono text-xs text-terracotta">0{i + 1}</p>
                                    <p className="mt-3 font-heading text-lg font-semibold tracking-tight">{step}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.3}>
                        <div className="mt-12 flex flex-wrap items-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="services-cta-button"
                                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                            >
                                {s.cta}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link to="/portfolio" data-testid="services-work-link" className="text-sm font-semibold text-muted-foreground transition-colors hover:text-primary dark:hover:text-teal">
                                {s.browseWork}
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Services;
